import { cn } from "app/lib/utils"
import ToolbarIconButton from "app/player/components/ToolbarIconButton"
import { cva, type VariantProps } from "class-variance-authority"
import { ComponentProps, forwardRef } from "react"

const playerIconButtonInnerClassName =
  "flex flex-1 items-center justify-center rounded-[12px] border border-solid border-gray-75 bg-white transition-colors group-hover:border-gray-200 group-hover:bg-gray-75 group-active:border-gray-200 group-active:bg-gray-75"

const playerIconButtonVariants = cva("", {
  variants: {
    color: {
      blue: "border-[1px] border-black bg-neutraal-900 text-white shadow-[0_0_4px_rgba(0,0,0,0.25)] data-[state=open]:bg-[linear-gradient(rgba(255,65,0,0.1),rgba(255,65,0,0.1)),linear-gradient(#03004e,#03004e)] data-[state=open]:hover:bg-[linear-gradient(rgba(255,65,0,0.3),rgba(255,65,0,0.3)),linear-gradient(#03004e,#03004e)] data-[state=open]:active:bg-[linear-gradient(rgba(255,65,0,0.2),rgba(255,65,0,0.2)),linear-gradient(#03004e,#03004e)]",
      white:
        "border-[1px] border-black bg-white text-neutraal-900 shadow-[0_0_4px_rgba(0,0,0,0.25)] data-[state=open]:bg-[linear-gradient(rgba(255,65,0,0.1),rgba(255,65,0,0.1)),linear-gradient(white,white)] data-[state=open]:hover:bg-[linear-gradient(rgba(255,65,0,0.3),rgba(255,65,0,0.3)),linear-gradient(white,white)] data-[state=open]:active:bg-[linear-gradient(rgba(255,65,0,0.2),rgba(255,65,0,0.2)),linear-gradient(white,white)]",
      gray: "group flex-col items-stretch border-[1px] border-gray-400 bg-gray-75 p-0 pb-1 text-neutraal-900 shadow-[0_2px_0_rgba(0,0,0,0.1)] hover:bg-gray-200 active:bg-gray-75",
    },
    toggled: {
      false: "",
      true: "",
    },
  },
  compoundVariants: [
    {
      color: "blue",
      toggled: false,
      class:
        "hover:bg-[linear-gradient(rgba(255,255,255,0.1),rgba(255,255,255,0.1)),linear-gradient(#03004e,#03004e)] active:bg-[linear-gradient(rgba(255,255,255,0.2),rgba(255,255,255,0.2)),linear-gradient(#03004e,#03004e)]",
    },
    {
      color: "blue",
      toggled: true,
      class:
        "bg-[linear-gradient(rgba(255,65,0,0.1),rgba(255,65,0,0.1)),linear-gradient(#03004e,#03004e)] hover:bg-[linear-gradient(rgba(255,65,0,0.3),rgba(255,65,0,0.3)),linear-gradient(#03004e,#03004e)] active:bg-[linear-gradient(rgba(255,65,0,0.2),rgba(255,65,0,0.2)),linear-gradient(#03004e,#03004e)]",
    },
    {
      color: "white",
      toggled: false,
      class:
        "hover:bg-[linear-gradient(rgba(255,255,255,0.1),rgba(255,255,255,0.1)),linear-gradient(white,white)] active:bg-[linear-gradient(rgba(255,255,255,0.2),rgba(255,255,255,0.2)),linear-gradient(white,white)]",
    },
    {
      color: "white",
      toggled: true,
      class:
        "bg-[linear-gradient(rgba(255,65,0,0.1),rgba(255,65,0,0.1)),linear-gradient(white,white)] hover:bg-[linear-gradient(rgba(255,65,0,0.3),rgba(255,65,0,0.3)),linear-gradient(white,white)] active:bg-[linear-gradient(rgba(255,65,0,0.2),rgba(255,65,0,0.2)),linear-gradient(white,white)]",
    },
  ],
  defaultVariants: {
    color: "blue",
    toggled: false,
  },
})

type Props = ComponentProps<typeof ToolbarIconButton> &
  VariantProps<typeof playerIconButtonVariants>

const PlayerIconButton = forwardRef<HTMLButtonElement, Props>(
  (
    {
      className,
      color = "blue",
      size = "md",
      toggled = false,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <ToolbarIconButton
        ref={ref}
        size={size}
        className={cn(playerIconButtonVariants({ color, toggled }), className)}
        {...props}
      >
        {color === "gray" ? (
          <span className={playerIconButtonInnerClassName}>{children}</span>
        ) : (
          children
        )}
      </ToolbarIconButton>
    )
  }
)

PlayerIconButton.displayName = "PlayerIconButton"

export default PlayerIconButton
